export interface ServiceLetter {
  consultant: string;
  project: string;
  file: string;
  preview: string;
}

export interface ServiceProjectRef {
  title: string;
  client: string;
  status: 'Completed' | 'Running';
}

export interface ServiceCategory {
  slug: string;
  title: string;
  short: string;
  summary: string;
  overview: string[];
  image: string;
  scope: string[];
  projects: ServiceProjectRef[];
  letters?: ServiceLetter[];
}

const mepLetter = (
  slug: string,
  consultant: string,
  project: string,
): ServiceLetter => ({
  consultant,
  project,
  file: `/assets/letters/mep/${slug}-full.webp`,
  preview: `/assets/letters/mep/${slug}.webp`,
});

export const SERVICES: ServiceCategory[] = [
  {
    slug: 'general-construction',
    title: 'General Construction',
    short:
      'Towers, schools, public buildings, villas and industrial complexes.',
    summary:
      'Building construction for government, commercial, residential and education clients across the UAE, from villa complexes to high-rise towers.',
    overview: [
      'Orient House delivers complete building packages: structure, envelope, finishes and external works. We handle design and build or build-only contracts, with our own site teams, plant and equipment.',
      'OHDC completed many significant projects with various types including educational projects, residential & commercial high-rise buildings, villa complexes & industrial projects.',
    ],
    image: '/assets/services/general-construction.webp',
    scope: [
      'Reinforced concrete & steel structures',
      'High-rise residential & commercial towers',
      'Schools, courts & public buildings',
      'Villa complexes & private residences',
      'Industrial complexes & warehouses',
      'Renovation & extension of existing buildings',
    ],
    projects: [
      {
        title: 'Residential Tower (B+G+M+P+30), Al Reem Island',
        client: 'Blue Lake Properties',
        status: 'Completed',
      },
      {
        title: 'Abu Dhabi Future Schools: Al Dhaher, Baniyas & Ain Al Fayda',
        client: 'ADEK',
        status: 'Completed',
      },
      {
        title: 'Design & Build of Baniyas Court',
        client: 'Abu Dhabi Judicial Department',
        status: 'Completed',
      },
      {
        title: 'Ghayathi Sports Club',
        client: 'ALDAR',
        status: 'Completed',
      },
      {
        title: 'Two Residential Buildings, Al Raha Beach RBW8',
        client: 'ADCE',
        status: 'Running',
      },
    ],
  },
  {
    slug: 'infrastructure-utilities',
    title: 'Infrastructure & Utilities',
    short: 'Roads, parking, utility networks and external works.',
    summary:
      'Roads, utility networks and external works that connect and serve communities, delivered on their own or alongside our building work.',
    overview: [
      'We build and upgrade the infrastructure around buildings and neighbourhoods: roads, parking, water and drainage networks, power and street lighting, and telecom ducting.',
      'Our teams coordinate closely with municipalities and utility authorities, managing NOCs, diversions and live-network interfaces from start to handover.',
    ],
    image: '/assets/services/infrastructure-utilities.webp',
    scope: [
      'Roads, pavements & parking structures',
      'Water supply, drainage & irrigation networks',
      'Power distribution & street lighting',
      'Telecom & ELV duct networks',
      'Hardscape, landscape & external works',
      'Utility diversions & network upgrades',
    ],
    projects: [
      {
        title: 'Upgrading of Sector E-6, Abu Dhabi City',
        client: 'Abu Dhabi Municipality',
        status: 'Completed',
      },
      {
        title: 'Infrastructure Networks Upgrade at 5 Locations',
        client: 'AD Ports',
        status: 'Running',
      },
      {
        title: '60 Villas & Related Infrastructure, Bida Al Mutawa',
        client: 'Musanada',
        status: 'Completed',
      },
      {
        title: '20 Villas & Related Infrastructure, Yabana – Jarra',
        client: 'Musanada',
        status: 'Completed',
      },
    ],
  },
  {
    slug: 'marine-topside-works',
    title: 'Marine & Topside Works',
    short:
      'Integrated marine design & build for ports, terminals and waterfront developments.',
    summary:
      'Design and build of topside facilities for ports, terminals and marinas, including the buildings, services and infrastructure on and around the waterfront.',
    overview: [
      'Delivering integrated marine design & build solutions for ports, terminals, and waterfront developments, including but not limited to rock revetment, floating pontoons, slipways and dredging, in addition to topside & infrastructure works.',
      'Waterfront sites leave little room for error. We plan logistics, marine interfaces and MEP work together so that facilities can open on schedule.',
    ],
    image: '/assets/services/marine-topside.webp',
    scope: [
      'Topside buildings: offices, retail & amenities',
      'Marine & waterfront structures',
      'Port warehouses & logistics facilities',
      'Yard paving, roads & services',
      'Utilities, lighting & MEP for port facilities',
    ],
    projects: [
      {
        title: 'Marine & Topside Works, Jebel Dhanna Terminal',
        client: 'AD Ports',
        status: 'Running',
      },
      {
        title: 'Topside Works, Rabdan / Al Alya / Saadiyat',
        client: 'AD Ports',
        status: 'Completed',
      },
      {
        title: 'Warehouses at Mugharraq Port',
        client: 'AD Ports',
        status: 'Running',
      },
    ],
  },
  {
    slug: 'special-projects',
    title: 'Special Projects',
    short: 'Security, defence, forensic and religious facilities.',
    summary:
      'Secure, specialised facilities for government and defence clients, where strict standards, confidentiality and close coordination are essential.',
    overview: [
      'OHDC successfully completed specialized projects with unique operational requirements and complex facility functions, requiring a high level of technical expertise and coordination. These projects involved stringent security requirements, specialized systems, and sophisticated equipment, delivered in accordance with the specific operational and performance standards of each facility.',
      'These buildings call for specialised systems, strict security procedures and experienced supervision. We bring all three to every special project.',
    ],
    image: '/assets/services/special-projects.webp',
    scope: [
      'Police, security & defence facilities',
      'Border posts & military camps',
      'Forensic & healthcare facilities',
      'Mosques & religious buildings',
      'Correctional facilities',
      'Sports & community venues',
    ],
    projects: [
      {
        title: 'AD Police Central Morgue, Al Shamkha South',
        client: 'Musanada / Abu Dhabi Police',
        status: 'Completed',
      },
      {
        title: 'Explosives & Weapons Center, Falaj Hazza',
        client: 'Abu Dhabi Police',
        status: 'Completed',
      },
      {
        title: 'Facilities at Ghweifat Border Post',
        client: 'Abu Dhabi Police',
        status: 'Completed',
      },
      {
        title: 'Al Wathba Prison: 10 New Buildings & 13 Renovations',
        client: 'Abu Dhabi Police',
        status: 'Completed',
      },
      {
        title: 'Al Reyadh City Mosques: 7 Mosques with Imam Houses',
        client: 'ALDAR',
        status: 'Running',
      },
    ],
  },
  {
    slug: 'mep-works',
    title: 'MEP Works',
    short: 'In-house mechanical, electrical and plumbing division.',
    summary:
      'Our in-house MEP division installs, tests and commissions mechanical, electrical and plumbing systems, and is approved by Abu Dhabi’s leading consultants.',
    overview: [
      'Orient House MEP has been approved as MEP subcontractor on towers, schools, mosques, villa complexes, industrial buildings and government facilities. Consultants including AECOM, Dar Al-Handasah, Al Suweidi, APG and CEG have signed off our work.',
      'Having MEP in-house means one team is accountable for coordination, testing and commissioning. It also cuts the interface risk between the civil and MEP packages.',
    ],
    image: '/assets/services/daralhandaseh.webp',
    scope: [
      'HVAC: chillers, AHUs/FAHUs, ducting & controls',
      'Power distribution, lighting & LV systems',
      'Plumbing, drainage & water supply',
      'Firefighting & fire alarm systems',
      'ELV, BMS & low-current systems',
      'Testing, commissioning & handover',
    ],
    projects: [
      {
        title: 'AD Police Central Morgue: Final MEP Taking Over',
        client: 'Musanada',
        status: 'Completed',
      },
      {
        title: 'Residential Tower, Reem Island',
        client: 'Blue Lake Properties',
        status: 'Completed',
      },
      {
        title: 'Sorouh Commercial Building (4B+G+M+6)',
        client: 'Sorouh',
        status: 'Completed',
      },
      {
        title: 'Two Residential Buildings, Al Raha Beach',
        client: 'ADCE',
        status: 'Running',
      },
    ],
    letters: [
      mepLetter(
        'musanada',
        'Musanada',
        'Construction of Abu Dhabi Police Central Morgue',
      ),
      mepLetter(
        'adce',
        'ADCE / Dar Al-Handasah',
        'Residential Buildings for H.H. Sheikha Mariam & H.H. Sheikha Shaikha',
      ),
      mepLetter(
        'aecom',
        'AECOM Middle East',
        'Upgrading of Sector E-6, Abu Dhabi',
      ),
      mepLetter(
        'alsuweidi',
        'Al Suweidi Engineering Consultants',
        'Sub-contractor submittal for MEP works',
      ),
      mepLetter(
        'apg',
        'Architecture & Planning Group',
        'Residential Tower for Blue Lake Properties, Reem Island',
      ),
      mepLetter(
        'carter',
        'Carter Goble Associates',
        'Refurbishment of Al Wathba Prison Facility',
      ),
      mepLetter(
        'dar-al-khaleej',
        'Dar Al Khaleej Engineering Consultants',
        'Sorouh Commercial Building',
      ),
      mepLetter(
        'ace-mosque',
        'Al Ain Consulting Engineers',
        'Al Mushrif Mosque, W-47',
      ),
      mepLetter(
        'ace',
        'Al Ain Consulting Engineers',
        'Commercial Building (G+M+4), E-19/2',
      ),
      mepLetter(
        'ceg',
        'Consulting Engineering Group',
        'Industrial Complex, ICAD III, Musaffah',
      ),
      mepLetter(
        'ceg-bld',
        'Consulting Engineering Group',
        'Commercial Building, ME-9, Plot C-116',
      ),
      mepLetter(
        'ceg-villa',
        'Consulting Engineering Group',
        'Villa Complex, Khalifa A City, SE-1',
      ),
      mepLetter(
        'iscg',
        'International Studies & Consultancy Group',
        'Commercial Building, ME-10, Musaffah',
      ),
    ],
  },
  {
    slug: 'facility-management',
    title: 'Facility Management',
    short: 'Maintenance, renovation and asset care after handover.',
    summary:
      'Keeping completed buildings safe, efficient and running through planned and reactive maintenance, renovation and asset care.',
    overview: [
      'Our facility management service carries our construction know-how past handover. It covers planned preventive maintenance, reactive repairs, and operating and maintaining building services.',
      'We have renovated and maintained occupied residential and commercial buildings across Abu Dhabi, planning the work so residents and tenants face as little disruption as possible.',
    ],
    image: '/assets/services/facility-management.webp',
    scope: [
      'Planned preventive maintenance',
      'Reactive & emergency repairs',
      'MEP systems operation & maintenance',
      'Building renovation & refurbishment',
      'Civil, finishing & fit-out upkeep',
      'Defects liability & warranty support',
    ],
    projects: [
      {
        title: 'Renovation & Maintenance, Residential Building G+M+14, E-13',
        client: 'Private',
        status: 'Completed',
      },
      {
        title: 'Renovation & Maintenance, Residential Building G+M+18, E-1',
        client: 'Private',
        status: 'Completed',
      },
      {
        title: 'Maintenance & Renovation, Building G+MF+5, Najda Street',
        client: 'Private',
        status: 'Completed',
      },
    ],
  },
];

export const findService = (slug: string | null) =>
  SERVICES.find((s) => s.slug === slug) ?? null;
